import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SignatureCanvasComponent from "./SignatureCanvas";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    zIndex: 1300,
  },
  "& .MuiBackdrop-root": {
    zIndex: 1299,
  },
}));

interface SignatureModalProps {
  open: boolean;
  onClose: () => void;
  onSaveSignature?: (signatureData: string) => void;
  onUseSignature: (signatureData: string) => void;
  signatureImage?: string | null;
}

const SignatureModal: React.FC<SignatureModalProps> = ({
  open,
  onClose,
  onSaveSignature,
  onUseSignature,
  signatureImage,
}) => {
  // Default to "Use Existing" tab (1) if there's a signature, otherwise "Draw Signature" tab (0)
  const [activeTab, setActiveTab] = useState(signatureImage ? 1 : 0);
  const [tempSignature, setTempSignature] = useState<string | null>(null);

  // Update active tab when signature image changes
  useEffect(() => {
    setActiveTab(signatureImage ? 1 : 0);
  }, [signatureImage]);

  const handleCanvasSave = async (signatureData: string) => {
    if (onSaveSignature) {
      onSaveSignature(signatureData);
      // Auto-populate the field with the newly created signature
      onUseSignature(signatureData);
      onClose();
      setTempSignature(null);
    }
  };

  const handleSignatureSave = (signatureData: string) => {
    if (onSaveSignature) {
      onSaveSignature(signatureData);
    }
    setTempSignature(signatureData);
  };

  const handleSignatureUse = (signatureData: string) => {
    onUseSignature(signatureData);
    onClose();
    setTempSignature(null);
  };

  const handleCancel = () => {
    onClose();
    setActiveTab(0);
    setTempSignature(null);
  };

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <StyledDialog
      open={open}
      onClose={handleCancel}
      maxWidth="md"
      fullWidth
      disableScrollLock={true}
      keepMounted={false}
      container={() => document.body}
    >
      <DialogTitle>Create or Update Signature</DialogTitle>
      <DialogContent>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          style={{ marginBottom: "20px" }}
        >
          <Tab label="Draw Signature" />
          <Tab label="Use Existing" />
        </Tabs>

        {activeTab === 0 && (
          <SignatureCanvasComponent
            onSave={handleCanvasSave}
            showSaveButton={true}
            onCancel={handleCancel}
          />
        )}

        {activeTab === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Use Existing Signature
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Select an existing signature from your account
            </Typography>
            {signatureImage ? (
              <Box
                style={{
                  border: "2px solid #e0e0e0",
                  borderRadius: "8px",
                  padding: "16px",
                  textAlign: "center",
                  marginTop: "16px",
                }}
              >
                <img
                  src={signatureImage}
                  alt="Existing Signature"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100px",
                    objectFit: "contain",
                  }}
                />
                <Typography variant="body2" style={{ marginTop: "8px" }}>
                  Existing signature from your profile
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary">
                No existing signatures found. Please create a new one using the
                "Draw Signature" tab.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="primary">
          Cancel
        </Button>
        {tempSignature && (
          <>
            <Button
              onClick={() => handleSignatureSave(tempSignature)}
              color="secondary"
              variant="outlined"
            >
              Save to Profile
            </Button>
            <Button
              onClick={() => handleSignatureUse(tempSignature)}
              color="primary"
              variant="contained"
            >
              Use Signature
            </Button>
          </>
        )}
        {activeTab === 1 && signatureImage && !tempSignature && (
          <Button
            onClick={() => handleSignatureUse(signatureImage)}
            color="primary"
            variant="contained"
          >
            Use Existing
          </Button>
        )}
      </DialogActions>
    </StyledDialog>
  );
};

export default SignatureModal;
